(() => {
  if (window.marameWelcomeCart) return;
  window.marameWelcomeCart = true;

  const mountSelector = '.marame-figma-footer__klaviyo';
  const formSelector = 'form[data-testid="klaviyo-form-U8Y5hR"]';
  const attempted = new Set();
  const results = new Map();
  const mounts = new Map();
  const resolving = new Set();
  const fingerprints = new Map();
  const memoryLedger = new Map();
  const key = (code) => code.toUpperCase();
  let controllerReady = false;

  const fingerprint = async (code) => {
    const identity = key(code);
    if (fingerprints.has(identity)) return fingerprints.get(identity);
    let value = '';
    try {
      const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(identity));
      value = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    } catch { /* Blocked crypto/storage falls back to in-page deduplication. */ }
    fingerprints.set(identity, value);
    return value;
  };
  const readLedger = (identity, hash) => {
    let value = memoryLedger.get(identity);
    try { if (!value && hash) value = window.sessionStorage.getItem(`marame.welcome-cart.${hash}`); } catch {}
    return ['applied', 'removed'].includes(value) ? value : '';
  };
  const remember = (code, action) => {
    if (!['applied', 'removed'].includes(action)) return;
    const identity = key(code);
    memoryLedger.set(identity, action);
    const hash = fingerprints.get(identity);
    // Session-scoped fingerprint/action only: never store the coupon or identity.
    try { if (hash) window.sessionStorage.setItem(`marame.welcome-cart.${hash}`, action); } catch {}
  };

  const readCode = (button) => {
    if (!button || !button.getClientRects().length || getComputedStyle(button).visibility === 'hidden') return '';
    const value = button.querySelector(':scope > div')?.textContent?.trim() || '';
    // Read only the actual provider coupon value, never labels, event metadata,
    // email addresses, preview placeholders, or another form's offer.
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{2,63}$/.test(value) || /(?:-PREVIEW)$|^(?:preview|loading|copied|copy|coupon|code)$/i.test(value)) return '';
    return value;
  };

  const statusText = (mount, status) => {
    if (status === 'applied') return mount.dataset.cartApplied || '';
    if (status === 'loading') return mount.dataset.cartPending || '';
    if (status === 'queued') return mount.dataset.cartReady || '';
    if (['removed', 'remembered', 'removing'].includes(status)) return '';
    // Acceptance without an allocated saving is not confirmed application.
    return status ? mount.dataset.cartError || '' : '';
  };

  const renderSuccess = (mount, form, button, code) => {
    const blocks = [...form.querySelectorAll('.klaviyo-form-richtext')].filter((block) => !block.contains(button) && !block.querySelector('input, select, textarea, button'));
    const first = blocks[0];
    if (!first) return;
    // Alter only the known success copy. Keep Klaviyo's real coupon/copy control.
    if (!first.querySelector('[data-welcome-copy]') && mount.dataset.successTitle && mount.dataset.successBody) {
      const copy = document.createElement('div');
      copy.dataset.welcomeCopy = '';
      for (const [label, className] of [['successTitle', 'title'], ['successBody', 'copy'], ['successNote', 'note']]) {
        if (!mount.dataset[label]) continue;
        const line = document.createElement('p');
        line.className = `marame-welcome__${className}`;
        line.textContent = mount.dataset[label];
        copy.append(line);
      }
      const status = document.createElement('p');
      status.className = 'marame-welcome__status';
      status.dataset.welcomeCartStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.hidden = true;
      copy.append(status);
      first.replaceChildren(copy);
    }
    const status = first.querySelector('[data-welcome-cart-status]');
    if (!status) return;
    const value = statusText(mount, results.get(key(code)));
    if (status.textContent !== value) status.textContent = value;
    if (status.hidden !== !value) status.hidden = !value;
  };

  const issue = async (code, retry = false) => {
    if (!controllerReady) return;
    const identity = key(code);
    if (resolving.has(identity)) return;
    if (attempted.has(identity) && !retry) return;
    if (['loading', 'queued'].includes(results.get(identity))) return;
    resolving.add(identity);
    const hash = await fingerprint(code);
    resolving.delete(identity);
    const previous = readLedger(identity, hash);
    if (!retry && previous) {
      attempted.add(identity);
      // A previous success prevents reapplication but is not fresh cart proof.
      results.set(identity, previous === 'removed' ? 'removed' : 'remembered');
      for (const mount of mounts.keys()) inspect(mount);
      return;
    }
    attempted.add(identity);
    results.set(identity, document.querySelector('marame-cart-discount')?.ready ? 'loading' : 'queued');
    // The existing controller preserves other discounts, submits Shopify's
    // locale-aware cart update, and renders only authoritative Shopify totals.
    document.dispatchEvent(new CustomEvent('marame:discount-issued', { detail: { code } }));
    for (const mount of mounts.keys()) inspect(mount);
  };

  const inspect = (mount) => {
    const form = mount.querySelector(formSelector);
    if (!form) return;
    const button = form.querySelector('.klaviyo-coupon button');
    const code = readCode(button);
    if (!code) return;
    issue(code);
    renderSuccess(mount, form, button, code);
  };

  const bind = () => {
    for (const [mount, observer] of mounts) {
      if (!mount.isConnected) { observer.disconnect(); mounts.delete(mount); }
    }
    document.querySelectorAll(mountSelector).forEach((mount) => {
      if (!mounts.has(mount)) {
        const observer = new MutationObserver(() => inspect(mount));
        observer.observe(mount, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
        mount.addEventListener('click', (event) => {
          const button = event.target.closest?.('.klaviyo-coupon button');
          const form = button?.closest(formSelector);
          if (!form || !mount.contains(form)) return;
          const code = readCode(button);
          if (!code) return;
          const status = results.get(key(code));
          // Preserve native copying; only a deliberate click retries a failure
          // or a code the shopper explicitly removed from their cart.
          if (!['applied', 'loading', 'queued'].includes(status)) issue(code, true);
          renderSuccess(mount, form, button, code);
        }, true);
        mounts.set(mount, observer);
      }
      inspect(mount);
    });
  };

  document.addEventListener('marame:discount-result', (event) => {
    const code = typeof event.detail?.code === 'string' ? event.detail.code.trim() : '';
    if (!code || !attempted.has(key(code))) return;
    const status = event.detail.status;
    if (typeof status !== 'string') return;
    results.set(key(code), status);
    remember(code, status);
    for (const mount of mounts.keys()) inspect(mount);
  });
  document.addEventListener('shopify:section:load', bind);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
  else bind();
  // Wait for the native discount controller, including its issued-code listener.
  // It queues the code if the bag is empty until an item creates the component.
  customElements.whenDefined('marame-cart-discount').then(() => { controllerReady = true; bind(); });
})();
